// This integrates built-in support for Union's membership, conversations, and analytics integration in the
// header component. Redux and its implementation should otherwise not be touched.

import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { membershipReducer } from '@xo-union/store-membership-redux';
import { identifyMemberMiddleware } from '@xo-union/tk-analytics-redux';
import conversationsReducer, { conversationsMiddleware } from '@xo-union/store-conversations-redux';
import type { PreloadedState } from '@reduxjs/toolkit';
import logoutMemberMiddleware from './middlewares/logoutMember';

const reducer = combineReducers({
  membership: membershipReducer,
  conversations: conversationsReducer,
});

const initialState = {
  membership: {},
  conversations: {},
};

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({
    reducer: reducer,
    preloadedState: preloadedState || initialState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Because Redux is only used by Union packages, we can
          // silence the serializable warnings outside our control.
          ignoreState: true,
          ignoreActions: true,
        },
      })
        /**
         * The logoutMemberMiddleware is responsible for redirecting the user to the homepage after Membership API's logout action
         * is successful.
         *
         */

        .prepend(logoutMemberMiddleware())
        /** From the docs: https://docs.union.theknot.com/docs/pattern-library/getting-started/global-state
         *
         * The `identifyMemberMiddleware` is responsible for calling Segment IO's
         * `identify`, where needed, with all necessary user traits. Segment IO's identify method
         * lets you tie a user to their actions and record traits about them.
         *
         * Under the hood, `identifyMemberMiddleware` gathers all necessary traits needed and
         * passes them to the `analytics.identify` method. It accomplishes this by:
         *
         *   1. Getting the current member data (Using the membership state)
         *   2. Getting the current count of unread messages (Using the conversations state)
         *   3. Assembling and formatting all necessary traits from steps 1 and 2 required to make the `analytics.identify` call.
         * */
        .prepend(identifyMemberMiddleware())

        /**
         * The conversations middleware is responsible for supporting the "Unread messages" feature
         * of the header nav
         * */
        .prepend(conversationsMiddleware()),
  });
};

const store = setupStore();

export default store;

export type RootState = ReturnType<typeof reducer>;
export type AppStore = ReturnType<typeof configureStore>;
